import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaUsers, FaCalendarAlt, FaChartLine, FaShieldAlt, FaMobileAlt, FaHeadset, FaQuoteLeft } from 'react-icons/fa';
import SEO from '../components/SEO';
import './Home.css';

const Home = ({ message }) => {
  // Ensure translations are loaded
  const { t } = useTranslation();
  const featuresRef = useRef(null);
  const featuresInView = useInView(featuresRef, { once: true, margin: "-100px" });

  const testimonialsRef = useRef(null);
  const testimonialsInView = useInView(testimonialsRef, { once: true, margin: "-100px" });

  return (
    <div className="home">
      <SEO
        titleKey="seo.home.title"
        descriptionKey="seo.home.description"
        image="/images/seo/HomepageMyCoachServer.png"
      />
      <main className="main-content">
        {/* Hero Section */}
        <motion.div
          className="hero-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          style={{
            background: `linear-gradient(rgba(15, 23, 42, 0.85), rgba(15, 23, 42, 0.95)), url(${process.env.PUBLIC_URL + '/images/seo/HeroCoachMyCoachServer.png'})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backdropFilter: 'blur(5px)'
          }}
        >
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {t('pages.home.title')} <span className="highlight">{t('pages.home.highlight')}</span>!
          </motion.h1>

          <motion.p
            className="subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {t('pages.home.subtitle')}
          </motion.p>

          {message && (
            <motion.p
              className="api-message"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              {t('pages.home.serverMessage')} {message}
            </motion.p>
          )}

          <motion.div
            className="auth-buttons-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <Link to="/login">
              <motion.button
                className="auth-btn login-btn"
                whileHover={{ scale: 1.05, y: -3 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('header.login')}
              </motion.button>
            </Link>
            <Link to="/register">
              <motion.button
                className="auth-btn register-btn"
                whileHover={{ scale: 1.05, y: -3 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('header.register')}
              </motion.button>
            </Link>
          </motion.div>

          {/* Animated Background Elements */}
          <div className="hero-decoration">
            <motion.div
              className="decoration-circle circle-1"
              animate={{
                y: [0, -20, 0],
                rotate: [0, 180, 360]
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "linear"
              }}
            />
            <motion.div
              className="decoration-circle circle-2"
              animate={{
                y: [0, 20, 0],
                rotate: [360, 180, 0]
              }}
              transition={{
                duration: 15,
                repeat: Infinity,
                ease: "linear"
              }}
            />
          </div>
        </motion.div>

        {/* Features Section */}
        <section className="features-section" ref={featuresRef}>
          <motion.h2
            className="section-title"
            initial={{ opacity: 0, y: 30 }}
            animate={featuresInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            {t('pages.home.featuresTitle')}
          </motion.h2>

          <div className="features-container">
            {[
              {
                icon: <FaUsers />,
                title: t('pages.home.feature1Title'),
                description: t('pages.home.feature1Desc'),
                delay: 0
              },
              {
                icon: <FaCalendarAlt />,
                title: t('pages.home.feature2Title'),
                description: t('pages.home.feature2Desc'),
                delay: 0.1
              },
              {
                icon: <FaChartLine />,
                title: t('pages.home.feature3Title'),
                description: t('pages.home.feature3Desc'),
                delay: 0.2
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 50 }}
                animate={featuresInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: feature.delay }}
                whileHover={{ y: -10, scale: 1.02 }}
              >
                <div className="feature-icon-modern">
                  {feature.icon}
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="testimonials-section" ref={testimonialsRef}>
          <motion.h2
            className="section-title"
            initial={{ opacity: 0, y: 30 }}
            animate={testimonialsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            {t('pages.home.testimonialsTitle')}
          </motion.h2>

          <div className="testimonials-grid">
            {[
              {
                text: t('pages.home.testimonial1Text'),
                author: t('pages.home.testimonial1Author'),
                role: t('pages.home.testimonial1Role')
              },
              {
                text: t('pages.home.testimonial2Text'),
                author: t('pages.home.testimonial2Author'),
                role: t('pages.home.testimonial2Role')
              },
              {
                text: t('pages.home.testimonial3Text'),
                author: t('pages.home.testimonial3Author'),
                role: t('pages.home.testimonial3Role')
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                className="testimonial-card"
                initial={{ opacity: 0, y: 50 }}
                animate={testimonialsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
              >
                <FaQuoteLeft className="quote-icon" />
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <div className="author-avatar">
                    {testimonial.author.charAt(0)}
                  </div>
                  <div className="author-info">
                    <h4>{testimonial.author}</h4>
                    <p>{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <motion.section
          className="cta-section"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2>{t('pages.home.ctaTitle')}</h2>
          <p>{t('pages.home.ctaSubtitle')}</p>
          <Link to="/register">
            <motion.button
              className="cta-button"
              whileHover={{ scale: 1.05, boxShadow: "0 10px 30px rgba(102, 126, 234, 0.5)" }}
              whileTap={{ scale: 0.95 }}
            >
              {t('pages.home.ctaButton')}
            </motion.button>
          </Link>
        </motion.section>
      </main>
    </div>
  );
};

export default Home;